import { ReloadOutlined } from '@ant-design/icons';
import { reportEvent, reportRenderError } from '@medcom/shared-ui/utils/telemetry.ts';
import { Button, Result } from 'antd';
import type React from 'react';
import { Suspense } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { LoadingIndicator } from './LoadingIndicator';

const LoadFailed: React.FC = () => (
  <Result
    status="warning"
    title="Dieser Bereich konnte nicht geladen werden"
    subTitle="Das passiert meist, wenn zwischenzeitlich eine neue Version veröffentlicht wurde."
    extra={
      <Button icon={<ReloadOutlined />} type="primary" onClick={() => window.location.reload()}>
        Neu laden
      </Button>
    }
  />
);

// This boundary catches everything thrown in the lazy subtree, not just the chunk fetch, and the
// two deserve opposite treatment. A hashed chunk that 404s because a deploy replaced the assets
// is expected and heals on reload — reporting it as an error would trip the frontend burst alert
// after every single release. A component that actually crashed in there is a real fault and must
// not hide behind "please reload".
const isStaleChunk = (error: unknown): error is Error =>
  error instanceof Error &&
  /dynamically imported module|Importing a module script failed|error loading dynamically imported/i.test(error.message);

const reportLoadFailure = (error: unknown, info: { componentStack?: string | null }): void => {
  if (isStaleChunk(error)) {
    // Counted, not alerted: how many users a release catches mid-session is worth knowing.
    reportEvent('lazy-chunk-stale', { error: error.message });
    return;
  }
  reportRenderError(error, info);
};

/**
 * Wraps a lazily loaded heavy dependency. Keeps a failed chunk fetch — e.g. after a deploy
 * replaced the hashed assets — contained in this one spot instead of letting it bubble into
 * the app-wide ErrorBoundary, which would blank the whole screen.
 */
export const LazyDependency: React.FC<{ children: React.ReactNode; height?: string }> = ({ children, height }) => (
  <ErrorBoundary FallbackComponent={LoadFailed} onError={reportLoadFailure}>
    <Suspense fallback={<LoadingIndicator height={height} />}>{children}</Suspense>
  </ErrorBoundary>
);
